import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// ============ FILTER-BASED BULK DELETE (price range, movement date, category) ============
export default function FilterDeleteDialog({ open, onOpenChange, categories, onDone }) {
  const [filters, setFilters] = useState({
    price_gte: "",
    price_lte: "",
    no_movement_before: "",
    category_slug: "",
  });
  const [excludeLocked, setExcludeLocked] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFilters({ price_gte: "", price_lte: "", no_movement_before: "", category_slug: "" });
      setExcludeLocked(true);
      setPreview(null);
    }
  }, [open]);

  const buildBody = (previewMode) => {
    const body = { preview: previewMode, exclude_locked: excludeLocked };
    if (filters.price_gte !== "") body.price_gte = parseFloat(filters.price_gte);
    if (filters.price_lte !== "") body.price_lte = parseFloat(filters.price_lte);
    if (filters.no_movement_before) body.no_movement_before = filters.no_movement_before;
    if (filters.category_slug) body.category_slug = filters.category_slug;
    return body;
  };

  const doPreview = async () => {
    const body = buildBody(true);
    const keys = Object.keys(body).filter((k) => !["preview", "exclude_locked"].includes(k));
    if (keys.length === 0) {
      toast.error("En az bir filtre gir");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/products/bulk-delete-by-filter", body);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!preview || preview.matched_count === 0) return;
    const n = preview.matched_count;
    if (!window.confirm(`🚨 ${n} ürün KALICI OLARAK silinecek VE kara listeye alınacak (bir sonraki Wolvox sync geri getiremez). Devam?`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/products/bulk-delete-by-filter", buildBody(false));
      toast.success(`✓ ${data.deleted} ürün silindi · 🚫 ${data.blacklisted} kara listeye alındı`);
      onOpenChange(false);
      // Wait for close animation before triggering parent reload (see
      // BulkMoveCategoryDialog for the full explanation of this race).
      setTimeout(() => onDone?.(), 240);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="filter-delete-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
            🧹 Filtre ile Toplu Sil
          </DialogTitle>
          <DialogDescription>
            Fiyat / hareket tarihi / kategoriye göre toplu silme. Silinen her ürünün akınsoft kodu <b>kara listeye</b> eklenir — bir sonraki Wolvox senkronizasyonu bunları atlar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Fiyat Aralığı (₺)</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="filter-price-gte"
                type="number"
                step="0.01"
                min="0"
                value={filters.price_gte}
                onChange={(e) => { setFilters((f) => ({ ...f, price_gte: e.target.value })); setPreview(null); }}
                placeholder="min — örn: 0,01"
                className="bg-white font-mono"
              />
              <span className="text-[#1B2A22]/60 font-mono">—</span>
              <Input
                data-testid="filter-price-lte"
                type="number"
                step="0.01"
                min="0"
                value={filters.price_lte}
                onChange={(e) => { setFilters((f) => ({ ...f, price_lte: e.target.value })); setPreview(null); }}
                placeholder="max — örn: 17,99"
                className="bg-white font-mono"
              />
            </div>
            <p className="text-[10px] text-[#1B2A22]/60 mt-1">
              Boş bırakılan sınır dikkate alınmaz. Örn: <b>0,01 — 17,99</b> → 0,01 ile 17,99 TL arası ürünler.
            </p>
          </div>
          <div>
            <Label className="text-xs">Son Hareket Tarihi ≤</Label>
            <Input
              data-testid="filter-movement-before"
              type="date"
              value={filters.no_movement_before}
              onChange={(e) => { setFilters((f) => ({ ...f, no_movement_before: e.target.value })); setPreview(null); }}
              className="bg-white"
            />
            <p className="text-[10px] text-[#1B2A22]/60 mt-1">
              Son 6 ay hareket görmemişleri için: bugünden 6 ay öncesini seç. Hareket tarihi Wolvox STOK_HAREKET'ten gelir.
            </p>
          </div>
          <div>
            <Label className="text-xs">Kategori</Label>
            <Select value={filters.category_slug} onValueChange={(v) => { setFilters((f) => ({ ...f, category_slug: v === "__all__" ? "" : v })); setPreview(null); }}>
              <SelectTrigger data-testid="filter-category" className="bg-white">
                <SelectValue placeholder="(Tümü)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">(Tüm kategoriler)</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.icon || "📦"} {c.name_tr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 p-3 rounded-xl bg-[#E5A024]/10 border border-[#E5A024]/40 cursor-pointer">
            <input
              type="checkbox"
              data-testid="exclude-locked"
              checked={excludeLocked}
              onChange={(e) => { setExcludeLocked(e.target.checked); setPreview(null); }}
              className="mt-1 w-4 h-4 accent-[#2E5A36]"
            />
            <div>
              <p className="text-sm font-heading font-bold text-[#B4711C]">
                🔒 Kilitli ürünlere dokunma (önerilen)
              </p>
              <p className="text-xs text-[#1B2A22]/70 mt-1">
                Daha önce kilitlediğin ürünler toplu silmeye dahil edilmez.
              </p>
            </div>
          </label>

          <Button
            data-testid="filter-preview-btn"
            onClick={doPreview}
            disabled={loading}
            variant="outline"
            className="w-full h-11 rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
          >
            {loading ? "Hesaplanıyor..." : "👁 Önizle (sayıyı göster)"}
          </Button>

          {preview && (
            <div className={`rounded-xl p-3 border-2 ${preview.matched_count === 0 ? "bg-[#2E5A36]/10 border-[#2E5A36]" : "bg-red-50 border-red-400"}`} data-testid="filter-preview-result">
              <p className="font-heading font-extrabold text-xl text-[#1B2A22]">
                {preview.matched_count === 0 ? "✓ Eşleşen yok" : `🚨 ${preview.matched_count} ürün silinecek`}
              </p>
              {preview.sample?.length > 0 && (
                <div className="mt-2 space-y-1 text-xs max-h-[200px] overflow-y-auto">
                  <p className="font-bold text-[#1B2A22]/70">İlk 10 örnek:</p>
                  {preview.sample.map((s, i) => (
                    <div key={i} className="flex justify-between font-mono bg-white px-2 py-1 rounded">
                      <span className="truncate flex-1">{s.name_tr}</span>
                      <span className="shrink-0 text-[#D9653B] font-bold">₺{s.price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {preview.matched_count > 0 && (
                <Button
                  data-testid="filter-confirm-delete-btn"
                  onClick={doDelete}
                  disabled={submitting}
                  className="w-full mt-3 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full font-heading font-bold"
                >
                  {submitting ? "Siliniyor..." : `🚨 ${preview.matched_count} Ürünü KALICI SİL + Kara Listeye Al`}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
